import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calculator, Check, Link2, Pencil, Users, X } from "lucide-react";
import {
  getMyTeam,
  listMyInvites,
  setMemberShareRate,
  setRecruiterBonus,
  updateTeamSettings,
  type AgentInvite,
  type TeamMember,
  type TeamResponse,
} from "../lib/agent";
import { Button, Card } from "../components/ui";
import { cn } from "../lib/cn";
import { AdminPreviewBanner, useIsAgent } from "../components/AdminPreviewBanner";
import { ShareButtons } from "../components/ShareButtons";
import { HelpBox, InfoTip } from "../components/InfoTip";

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function fmtPct(rate: number): string {
  return `${Math.round(rate * 1000) / 10} %`.replace(".", ",");
}

const STATUS_STYLE: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-900",
  pending: "bg-amber-100 text-amber-900",
  suspended: "bg-sky-100 text-sky-900",
  terminated: "bg-rose-100 text-rose-900",
};

export function AgentTeam() {
  const userIsAgent = useIsAgent();
  const { data, isLoading } = useQuery<TeamResponse>({
    queryKey: ["agent-team"],
    queryFn: () => getMyTeam(),
    enabled: userIsAgent,
  });

  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Mein Team</h2>
        <p className="mt-2 text-sm text-ink-muted">
          {data?.is_master
            ? "Deine Team-Mitglieder — du bestimmst, was jeder für Bestellungen seiner Kunden bekommt."
            : "Dein Team auf einen Blick."}
        </p>
      </div>

      {!userIsAgent && <AdminPreviewBanner pageName="Team" />}

      {userIsAgent && data?.is_master && (
        <div className="mb-4">
          <HelpBox kind="info" collapsible title="👥 Wie funktioniert das Team-System?">
            Du bist <strong>Master-Agent</strong>. Jeder, der über einen Link
            aus deinem Team Agent wird, landet <strong>direkt bei dir</strong> —
            egal ob du selbst oder ein Mitglied ihn geworben hat. Es gibt keine
            Ketten und keine Unter-Ebenen.
            <br />
            <br />
            <strong>Provisionen:</strong> Bestellt ein Kunde eines Mitglieds,
            bekommt das Mitglied den Prozentsatz, den <strong>du</strong> für
            ihn festgelegt hast — den Rest bekommst du. Bestellungen deiner
            eigenen Kunden gehen komplett an dich.
            <br />
            <br />
            <strong>Prozente:</strong> Lege unten pro Mitglied einen Satz fest
            (0–100&nbsp;%) oder nutze deinen Team-Standard. Änderungen gelten
            ab der nächsten Bestellung.
            <br />
            <br />
            <strong>Werber-Bonus:</strong> Hat ein Mitglied ein anderes Mitglied
            geworben, kannst du dem Werber X&nbsp;% <strong>von deinem
            Anteil</strong> aus den Verkäufen des Geworbenen abgeben. Der Bonus
            kommt aus deiner Tasche — der Verkäufer behält immer seinen vollen
            Satz. Rechne es unten im Demo-Rechner durch.
          </HelpBox>
        </div>
      )}

      {userIsAgent && data && !data.is_master && (
        <MemberView data={data} />
      )}

      <RecruitmentLinkCard
        isMaster={data?.is_master ?? false}
        masterName={data?.master_first_name}
        joinSlug={data?.join_slug}
      />

      {isLoading && <div className="text-sm text-ink-muted">Lade Team…</div>}

      {data?.is_master && <TeamSettingsCard data={data} />}

      {data?.is_master && <DemoCalculatorCard data={data} />}

      {data?.is_master && data.members.length === 0 && (
        <Card className="text-center py-10">
          <Users className="size-8 text-ink-muted mx-auto mb-2" />
          <p className="text-sm text-ink-muted">
            Noch keine Team-Mitglieder. Teile deinen Einladungs-Link weiter oben.
          </p>
        </Card>
      )}

      {data?.is_master && data.members.length > 0 && <MemberTable data={data} />}
    </main>
  );
}

/** Mitglied-Sicht: Master + eigener Satz (falls sichtbar geschaltet). */
function MemberView({ data }: { data: TeamResponse }) {
  return (
    <Card className="mb-6">
      <div className="flex items-start gap-3">
        <Users className="size-4 text-accent-orange flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">
            Du bist im Team von {data.master_first_name || "deinem Master"}
          </div>
          {data.my_share_rate != null ? (
            <p className="mt-1 text-xs text-ink-muted">
              Du bekommst aktuell{" "}
              <strong className="text-ink">{fmtPct(data.my_share_rate)}</strong>{" "}
              von {data.master_first_name}s Provision auf Bestellungen deiner
              Kunden. Den Satz legt {data.master_first_name} fest.
            </p>
          ) : (
            <p className="mt-1 text-xs text-ink-muted">
              Deinen Provisions-Anteil legt {data.master_first_name || "dein Master"}{" "}
              individuell fest — sprich ihn bei Fragen direkt an.
            </p>
          )}
          <p className="mt-2 text-xs text-ink-muted">
            Du kannst selbst neue Agenten einladen — sie werden Teil von{" "}
            {data.master_first_name || "deinem Master"}s Team.
          </p>
          <p className="mt-2 text-xs text-ink-muted">
            Deinen Vorteil, wenn du jemanden wirbst (Werber-Bonus), besprichst
            du direkt mit {data.master_first_name || "deinem Master"} — er legt
            fest, wie viel Prozent von seinem Anteil du aus den Verkäufen deiner
            Geworbenen bekommst.
          </p>

          {data.my_share_rate != null && (
            <div className="mt-3 pt-3 border-t border-line">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
                So fließt das Geld — Beispiel mit 100 € Provisions-Topf
              </div>
              <ProvisionFlow
                pool={100}
                memberRate={data.my_share_rate}
                bonusRate={0}
                sellerLabel="Du (Verkäufer)"
                masterLabel={`${data.master_first_name || "Master"}s Anteil`}
                masterKeepsLabel={`${data.master_first_name || "Master"} behält`}
              />
              <p className="mt-2 text-[11px] text-ink-muted">
                Der Topf ist das Geld, das aus einer Bestellung deiner Kunden in{" "}
                {data.master_first_name || "deinem Master"}s Team fließt. Dein
                Anteil geht immer zuerst ab — der Rest gehört{" "}
                {data.master_first_name || "deinem Master"}.
              </p>
            </div>
          )}

          {(data.my_recruits?.length ?? 0) > 0 && (
            <div className="mt-3 pt-3 border-t border-line">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1">
                Von dir geworben
              </div>
              <ul className="space-y-0.5">
                {data.my_recruits!.map((r, i) => (
                  <li key={i} className="text-xs text-ink-muted">
                    {r.name}
                    {r.bonus_rate != null && r.bonus_rate > 0 && (
                      <>
                        {" — du erhältst "}
                        <strong className="text-ink">{fmtPct(r.bonus_rate)}</strong>
                        {` von ${data.master_first_name || "deinem Master"}s Anteil aus seinen Verkäufen (Werber-Bonus)`}
                      </>
                    )}
                    {r.bonus_rate === 0 && " — aktuell kein Werber-Bonus"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Master: Team-Standard-Satz + Sichtbarkeits-Schalter. */
function TeamSettingsCard({ data }: { data: TeamResponse }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { default_share_rate?: number; clear_default?: boolean; share_visible?: boolean }) =>
      updateTeamSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-team"] });
      setEditing(false);
    },
  });

  const effective = data.effective_default_share_rate ?? 0.4;

  return (
    <Card className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="text-sm">
          <div className="font-semibold flex items-center gap-1.5">
            Team-Standard-Satz
            <InfoTip title="Was ist das?">
              Dieser Satz gilt für jedes Mitglied, dem du keinen individuellen
              Satz gegeben hast — auch für alle Neuen. 0–100&nbsp;% des
              Provisions-Topfs.
            </InfoTip>
          </div>
          <p className="text-xs text-ink-muted mt-0.5">
            Neue Mitglieder starten mit{" "}
            <strong className="text-ink">{fmtPct(effective)}</strong>
            {data.team_default_share_rate == null && " (System-Standard)"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-24 rounded-lg border border-line px-2 py-1.5 text-sm text-right"
                placeholder="%"
              />
              <span className="text-xs text-ink-muted">%</span>
              <Button
                disabled={mutation.isPending || value === ""}
                onClick={() => {
                  const pct = parseFloat(value.replace(",", "."));
                  if (Number.isNaN(pct) || pct < 0 || pct > 100) return;
                  mutation.mutate({ default_share_rate: pct / 100 });
                }}
              >
                Speichern
              </Button>
              <Button variant="secondary" onClick={() => setEditing(false)}>
                Abbrechen
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setValue(String(Math.round(effective * 1000) / 10));
                setEditing(true);
              }}
            >
              <Pencil className="size-3.5" /> Standard ändern
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-line flex items-start justify-between gap-3">
        <div className="text-xs text-ink-muted">
          <strong className="text-ink">Sätze für Mitglieder sichtbar?</strong>{" "}
          Wenn an, sieht jedes Mitglied seinen eigenen Satz („Du bekommst X&nbsp;% von
          deiner Provision") — nie die Sätze der anderen.
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate({ share_visible: !data.share_visible })}
          disabled={mutation.isPending}
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition",
            data.share_visible ? "bg-accent-orange" : "bg-slate-300",
          )}
          title={data.share_visible ? "Sichtbar — klicken zum Verbergen" : "Verborgen — klicken zum Anzeigen"}
        >
          <span
            className={cn(
              "inline-block size-4 rounded-full bg-white transition",
              data.share_visible ? "translate-x-6" : "translate-x-1",
            )}
          />
        </button>
      </div>
    </Card>
  );
}

/** Gemeinsame Geldfluss-Grafik: Pool → Verkäufer + Master-Rest (→ Werber-Bonus).
 *  Rechnet exakt wie das Backend — Master- und Mitglied-Sicht nutzen dieselbe
 *  Grafik, nur mit anderen Beschriftungen. */
function ProvisionFlow({
  pool,
  memberRate,
  bonusRate,
  sellerLabel,
  masterLabel,
  masterKeepsLabel,
}: {
  pool: number;
  memberRate: number; // 0..1 vom Pool
  bonusRate: number; // 0..1 vom Master-Rest; 0 = Bonus-Zeile ausblenden
  sellerLabel: string;
  masterLabel: string;
  masterKeepsLabel: string;
}) {
  const memberEur = pool * memberRate;
  const masterRest = pool - memberEur;
  const bonusEur = masterRest * bonusRate;
  const masterEur = masterRest - bonusEur;
  const fmt = (v: number) =>
    v.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  return (
    <div className="text-xs">
      <div className="mx-auto max-w-xs rounded-lg border border-line bg-cream-soft/50 px-3 py-2 text-center">
        <div className="font-semibold">Bestellung → Provisions-Topf</div>
        <div className="font-mono text-sm font-semibold">{fmt(pool)}</div>
      </div>
      <div className="text-center text-ink-muted my-1.5">↓ wird aufgeteilt</div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
          <div className="font-semibold text-sky-900">{sellerLabel}</div>
          <div className="font-mono text-sm font-semibold text-sky-900">{fmt(memberEur)}</div>
          <div className="text-[10px] text-sky-900/70 mt-0.5">
            {fmtPct(memberRate)} vom Topf — immer zuerst
          </div>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
          <div className="font-semibold text-orange-900">{masterLabel}</div>
          <div className="font-mono text-sm font-semibold text-orange-900">{fmt(masterRest)}</div>
          <div className="text-[10px] text-orange-900/70 mt-0.5">der Rest des Topfs</div>
        </div>
      </div>
      {bonusRate > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div />
            <div className="text-center text-ink-muted my-1.5">↓ davon geht ab</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
              <div className="font-semibold text-violet-900">Werber-Bonus</div>
              <div className="font-mono text-sm font-semibold text-violet-900">{fmt(bonusEur)}</div>
              <div className="text-[10px] text-violet-900/70 mt-0.5">
                {fmtPct(bonusRate)} von {fmt(masterRest)} — an den Werber
              </div>
            </div>
            <div className="rounded-lg border border-orange-300 bg-orange-100/70 px-3 py-2">
              <div className="font-semibold text-orange-900">{masterKeepsLabel}</div>
              <div className="font-mono text-sm font-semibold text-orange-900">{fmt(masterEur)}</div>
              <div className="text-[10px] text-orange-900/70 mt-0.5">
                {fmt(masterRest)} − {fmt(bonusEur)}
              </div>
            </div>
          </div>
        </>
      )}
      {pool > 0 && (
        <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full border border-line">
          <div className="bg-sky-400" style={{ width: `${(memberEur / pool) * 100}%` }} />
          {bonusRate > 0 && (
            <div className="bg-violet-400" style={{ width: `${(bonusEur / pool) * 100}%` }} />
          )}
          <div className="bg-accent-orange" style={{ width: `${(masterEur / pool) * 100}%` }} />
        </div>
      )}
    </div>
  );
}

/** Master: Beispiel-Rechner — zeigt live, wie sich eine Provision aufteilt. */
function DemoCalculatorCard({ data }: { data: TeamResponse }) {
  const [open, setOpen] = useState(false);
  const [poolStr, setPoolStr] = useState("100");
  const defaultPct = Math.round((data.effective_default_share_rate ?? 0.4) * 1000) / 10;
  const [memberPctStr, setMemberPctStr] = useState(String(defaultPct));
  const [bonusPctStr, setBonusPctStr] = useState("10");

  const parse = (s: string) => {
    const v = parseFloat(s.replace(",", "."));
    return Number.isNaN(v) ? 0 : v;
  };
  const pool = Math.max(0, parse(poolStr));
  const memberRate = Math.min(100, Math.max(0, parse(memberPctStr))) / 100;
  const bonusRate = Math.min(100, Math.max(0, parse(bonusPctStr))) / 100;

  const inputCls =
    "w-24 rounded-lg border border-line px-2 py-1.5 text-sm text-right";

  return (
    <Card className="mb-6">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Calculator className="size-4 text-accent-orange" />
          Demo-Rechner: So teilt sich eine Provision auf
        </div>
        <span className="text-xs text-ink-muted whitespace-nowrap flex-shrink-0">{open ? "einklappen" : "ausklappen"}</span>
      </button>

      {open && (
        <div className="mt-4">
          <p className="text-xs text-ink-muted mb-3">
            Spiel es durch: Ein Kunde eines Mitglieds bestellt, und in dein Team
            fließt der Provisions-Topf. Das Mitglied bekommt seinen Satz vom
            Topf — vom <strong>Rest (deinem Anteil)</strong> geht der
            Werber-Bonus ab, falls das Mitglied von jemandem geworben wurde.
          </p>

          <div className="flex flex-wrap gap-4 mb-4">
            <label className="text-xs text-ink-muted">
              <span className="block mb-1 font-medium">Provisions-Topf</span>
              <span className="inline-flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={poolStr}
                  onChange={(e) => setPoolStr(e.target.value)}
                  className={inputCls}
                />
                €
              </span>
            </label>
            <label className="text-xs text-ink-muted">
              <span className="block mb-1 font-medium">Anteil des Mitglieds</span>
              <span className="inline-flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  value={memberPctStr}
                  onChange={(e) => setMemberPctStr(e.target.value)}
                  className={inputCls}
                />
                %
              </span>
            </label>
            <label className="text-xs text-ink-muted">
              <span className="block mb-1 font-medium">Werber-Bonus (von deinem Anteil)</span>
              <span className="inline-flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  value={bonusPctStr}
                  onChange={(e) => setBonusPctStr(e.target.value)}
                  className={inputCls}
                />
                %
              </span>
            </label>
          </div>

          <ProvisionFlow
            pool={pool}
            memberRate={memberRate}
            bonusRate={bonusRate}
            sellerLabel="Mitglied (Verkäufer)"
            masterLabel="Dein Anteil"
            masterKeepsLabel="Du behältst"
          />

          <p className="mt-3 text-[11px] text-ink-muted">
            Hat das Mitglied keinen Werber (du hast es selbst geworben), entfällt
            der Bonus — du behältst deinen Anteil komplett.
          </p>
        </div>
      )}
    </Card>
  );
}

/** Master: Mitglieder — Karten auf Mobile, Tabelle ab sm. */
function MemberTable({ data }: { data: TeamResponse }) {
  return (
    <>
      <div className="sm:hidden space-y-3">
        {data.members.map((m) => (
          <MemberCard key={m.id} member={m} />
        ))}
      </div>
      <div className="hidden sm:block overflow-x-auto rounded-2xl border border-line bg-white">
      <table className="w-full text-sm min-w-[860px]">
        <thead className="bg-cream-soft/40 text-ink-muted">
          <tr>
            <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Mitglied</th>
            <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Status</th>
            <th className="text-left font-medium px-3 py-2 whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                Geworben von
                <InfoTip title="Nur zur Info" maxWidth={220}>
                  Wer das Mitglied eingeladen hat. Strukturell hängen alle
                  direkt bei dir — belohne fleißige Werber über ihre Sätze.
                </InfoTip>
              </span>
            </th>
            <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Seit</th>
            <th className="text-right font-medium px-3 py-2 whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                Sein Anteil
                <InfoTip title="Anteil am Provisions-Topf" maxWidth={240}>
                  Was das Mitglied für Bestellungen seiner Kunden bekommt.
                  Klick zum Ändern (0–100&nbsp;%).
                </InfoTip>
              </span>
            </th>
            <th className="text-right font-medium px-3 py-2 whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                Werber-Bonus
                <InfoTip title="Bonus für den Werber" maxWidth={280}>
                  X&nbsp;% <strong>von DEINEM Anteil</strong> aus den Verkäufen
                  DIESES Mitglieds gehen an seinen Werber („Geworben von").
                  Der Bonus kommt aus deiner Tasche, der Verkäufer behält
                  seinen vollen Satz. Nur möglich, wenn nicht du selbst der
                  Werber bist.
                </InfoTip>
              </span>
            </th>
            <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Dein Verdienst daraus</th>
          </tr>
        </thead>
        <tbody>
          {data.members.map((m) => (
            <MemberRow key={m.id} member={m} />
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

/** Inline-Editor: Anteil des Mitglieds — genutzt in Tabelle UND Mobile-Karte. */
function ShareRateEditor({ m }: { m: TeamMember }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const mutation = useMutation({
    mutationFn: (rate: number | null) => setMemberShareRate(m.id, rate),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-team"] });
      setEditing(false);
    },
  });

  return (
    <>
      {editing ? (
        <span className="inline-flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-20 rounded-lg border border-line px-2 py-1 text-xs text-right"
            autoFocus
          />
          <span className="text-[10px] text-ink-muted">%</span>
          <button
            type="button"
            className="text-emerald-700 hover:text-emerald-900 disabled:opacity-40 tap-target"
            disabled={mutation.isPending || value === ""}
            title="Speichern"
            onClick={() => {
              const pct = parseFloat(value.replace(",", "."));
              if (Number.isNaN(pct) || pct < 0 || pct > 100) return;
              mutation.mutate(pct / 100);
            }}
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            className="text-ink-muted hover:text-ink tap-target"
            title="Abbrechen"
            onClick={() => setEditing(false)}
          >
            <X className="size-4" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => {
            setValue(String(Math.round(m.effective_share_rate * 1000) / 10));
            setEditing(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-cream-soft font-mono font-semibold text-xs"
          title="Satz ändern"
        >
          {fmtPct(m.effective_share_rate)}
          {m.share_rate == null && (
            <span className="text-[9px] font-sans font-normal text-ink-muted">(Standard)</span>
          )}
          <Pencil className="size-3 text-ink-muted" />
        </button>
      )}
      {m.share_rate != null && !editing && (
        <div>
          <button
            type="button"
            className="text-[10px] text-ink-muted hover:text-ink hover:underline"
            onClick={() => mutation.mutate(null)}
            disabled={mutation.isPending}
            title="Individuellen Satz entfernen — Team-Standard gilt wieder"
          >
            auf Standard zurücksetzen
          </button>
        </div>
      )}
    </>
  );
}

/** Inline-Editor: Werber-Bonus — genutzt in Tabelle UND Mobile-Karte. */
function BonusEditor({ m }: { m: TeamMember }) {
  const qc = useQueryClient();
  const [bonusEditing, setBonusEditing] = useState(false);
  const [bonusValue, setBonusValue] = useState("");
  const [bonusError, setBonusError] = useState<string | null>(null);

  const bonusMutation = useMutation({
    mutationFn: (rate: number | null) => setRecruiterBonus(m.id, rate),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-team"] });
      setBonusEditing(false);
      setBonusError(null);
    },
    onError: (e) => setBonusError(e instanceof Error ? e.message : "Fehler"),
  });

  if (!m.bonus_editable) {
    return (
      <span className="text-[10px] text-ink-muted" title="Von dir selbst geworben — kein Werber zu belohnen">
        —
      </span>
    );
  }

  return (
    <>
      {bonusEditing ? (
        <span className="inline-flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={bonusValue}
            onChange={(e) => setBonusValue(e.target.value)}
            className="w-20 rounded-lg border border-line px-2 py-1 text-xs text-right"
            autoFocus
          />
          <span className="text-[10px] text-ink-muted">%</span>
          <button
            type="button"
            className="text-emerald-700 hover:text-emerald-900 disabled:opacity-40 tap-target"
            disabled={bonusMutation.isPending || bonusValue === ""}
            title="Speichern"
            onClick={() => {
              const pct = parseFloat(bonusValue.replace(",", "."));
              if (Number.isNaN(pct) || pct < 0 || pct > 100) return;
              bonusMutation.mutate(pct / 100);
            }}
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            className="text-ink-muted hover:text-ink tap-target"
            title="Abbrechen"
            onClick={() => {
              setBonusEditing(false);
              setBonusError(null);
            }}
          >
            <X className="size-4" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => {
            setBonusValue(
              m.recruiter_bonus_rate != null
                ? String(Math.round(m.recruiter_bonus_rate * 1000) / 10)
                : "",
            );
            setBonusEditing(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-cream-soft font-mono font-semibold text-xs"
          title={`Werber-Bonus an ${m.recruited_by_name || "den Werber"} ändern`}
        >
          {m.recruiter_bonus_rate != null && m.recruiter_bonus_rate > 0 ? (
            fmtPct(m.recruiter_bonus_rate)
          ) : (
            <span className="text-ink-muted font-sans font-normal">kein Bonus</span>
          )}
          <Pencil className="size-3 text-ink-muted" />
        </button>
      )}
      {!bonusEditing && m.recruiter_bonus_rate != null && m.recruiter_bonus_rate > 0 && (
        <div>
          <button
            type="button"
            className="text-[10px] text-ink-muted hover:text-ink hover:underline"
            onClick={() => bonusMutation.mutate(null)}
            disabled={bonusMutation.isPending}
            title="Werber-Bonus beenden"
          >
            Bonus beenden
          </button>
        </div>
      )}
      {bonusError && (
        <div className="text-[10px] text-rose-700 max-w-40">{bonusError}</div>
      )}
    </>
  );
}

function statusLabel(status: TeamMember["status"]): string {
  return status === "active" ? "Aktiv" : status === "pending" ? "Ausstehend" : status;
}

function MemberRow({ member: m }: { member: TeamMember }) {
  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2">
        <div className="font-medium text-xs">{m.name}</div>
        <div className="text-[11px] font-mono text-ink-muted">{m.agent_code}</div>
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium",
            STATUS_STYLE[m.status] || "bg-slate-200 text-slate-700",
          )}
        >
          {statusLabel(m.status)}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">
        {m.recruited_by_name || "—"}
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">
        {new Date(m.joined_at).toLocaleDateString("de-DE")}
      </td>
      <td className="px-3 py-2 text-right">
        <ShareRateEditor m={m} />
      </td>
      <td className="px-3 py-2 text-right">
        <BonusEditor m={m} />
      </td>
      <td className="px-3 py-2 text-right font-mono font-semibold">
        {m.my_total_cents > 0 ? fmtEur(m.my_total_cents) : "—"}
      </td>
    </tr>
  );
}

/** Mobile: ein Mitglied als Karte — alle Infos ohne Quer-Scroll editierbar. */
function MemberCard({ member: m }: { member: TeamMember }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{m.name}</div>
          <div className="text-[11px] font-mono text-ink-muted truncate">{m.agent_code}</div>
        </div>
        <span
          className={cn(
            "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium flex-shrink-0",
            STATUS_STYLE[m.status] || "bg-slate-200 text-slate-700",
          )}
        >
          {statusLabel(m.status)}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-ink-muted">
        Geworben von {m.recruited_by_name || "—"} · seit{" "}
        {new Date(m.joined_at).toLocaleDateString("de-DE")}
      </div>
      <div className="mt-2 pt-2 border-t border-line/70 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted mb-1">
            Sein Anteil
          </div>
          <ShareRateEditor m={m} />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted mb-1">
            Werber-Bonus
          </div>
          <BonusEditor m={m} />
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-line/70 flex items-center justify-between text-xs">
        <span className="text-ink-muted">Dein Verdienst daraus</span>
        <strong className="font-mono">
          {m.my_total_cents > 0 ? fmtEur(m.my_total_cents) : "—"}
        </strong>
      </div>
    </div>
  );
}

function RecruitmentLinkCard({
  isMaster,
  masterName,
  joinSlug,
}: {
  isMaster: boolean;
  masterName?: string | null;
  joinSlug?: string;
}) {
  const { data: invites } = useQuery<AgentInvite[]>({
    queryKey: ["agent-invites"],
    queryFn: () => listMyInvites(),
  });
  const defaultInvite = invites?.find((i) => i.is_default);
  // Team-Link trägt den Klarnamen (/join/vorname-nachname); Fallback: Invite-Code
  const recruitmentUrl = joinSlug
    ? `https://agenten.dpconnect.de/join/${joinSlug}`
    : defaultInvite
      ? `https://agenten.dpconnect.de/join?ref=${defaultInvite.invite_code}`
      : null;

  if (!recruitmentUrl) return null;

  return (
    <Card className="mb-6">
      <div className="flex items-start gap-3 mb-3">
        <Link2 className="size-4 text-accent-orange flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold">
            Dein Einladungs-Link für neue Agenten
          </h3>
          <ul className="text-xs text-ink-muted mt-1 space-y-1 list-disc list-inside">
            <li>
              Nur fürs <strong className="text-ink">Anwerben neuer Agenten</strong> —
              nicht für Kunden-Verkäufe.
            </li>
            <li>
              Öffnet direkt das <strong className="text-ink">Registrierungs-Formular</strong> —
              deine Einladung ist automatisch eingetragen, dein Name steht dort
              als Einladender. Kein Code zum Abtippen.
            </li>
            <li>
              {isMaster
                ? "Wer sich registriert, wird nach Freischaltung Mitglied deines Teams."
                : `Wer sich registriert, wird Mitglied von ${masterName || "deinem Master"}s Team — du wirst als Werber vermerkt (Werber-Bonus).`}
            </li>
          </ul>
        </div>
      </div>
      <div className="rounded-lg bg-white border border-line px-3 py-2 text-[11px] font-mono break-all mb-2">
        {recruitmentUrl}
      </div>
      <a
        href={recruitmentUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-block text-xs text-accent-orange hover:underline mb-2"
      >
        Vorschau öffnen — so sieht es dein Kontakt →
      </a>
      {joinSlug && (
        <p className="text-xs text-ink-muted mb-3">
          Dein Einladungs-Code:{" "}
          <code className="font-mono bg-white border border-line rounded px-1.5 py-0.5 text-ink">
            {joinSlug}
          </code>{" "}
          — kommt der Link mal nicht an, trägt dein Kontakt diesen Code bei der
          Registrierung („Agent werden") einfach von Hand ins Feld
          „Einladungs-Code" ein.
        </p>
      )}
      <ShareButtons
        url={recruitmentUrl}
        emailSubject="Werde Empfehlungs-Agent bei DP Connect"
        qrFilename="dp-team-einladung"
      />
    </Card>
  );
}
